/**
 * useRebookDrawer — everything the rebook drawer needs to assemble before it can
 * render any of its three screens (BON-146): the month-scoped targets fetch, the
 * browse/form toggle, the calendar view mode, and the submit lifecycle.
 *
 * Split from the component so that file is the three screens and nothing else.
 * The targets fetch lives HERE rather than in the picker because the drawer
 * needs the envelope before the picker ever opens — the cutoff verdict decides
 * which notice the form shows.
 */
import { useState } from 'react';

import type { BookingDetail } from '../../api/bookingsV2';
import { usePermissions } from '../../hooks/usePermissions';
import { useViewport } from '../../hooks/useViewport';
import { Permission } from '../../types/permissions';

import type { PickerViewMode } from './shared/pickerViewMode';
import { usePickerMonth } from './shared/usePickerMonth';
import { useRebookTargets } from './shared/useRebookTargets';
import { formatPickerDate, formatPickerTime } from './shared/WorkshopPickerHelpers';
import { useRebookBookingSubmit } from './useRebookBookingSubmit';

export function useRebookDrawer(
  booking: BookingDetail,
  onClose: () => void,
  onRebooked: () => void,
) {
  const { isDesktop } = useViewport();
  const { hasPermission } = usePermissions();
  const { month, setMonth } = usePickerMonth(booking.workshop.localStartDatetime);
  const targets = useRebookTargets(booking.id, booking.workshop.id, month);
  const [browsing, setBrowsing] = useState(false);
  const [viewMode, setViewMode] = useState<PickerViewMode>(() => (isDesktop ? 'calendar' : 'list'));

  const local = booking.workshop.localStartDatetime;
  const time = formatPickerTime(local);
  const date = formatPickerDate(local);

  const submit = useRebookBookingSubmit({
    bookingId: booking.id,
    // False until the envelope lands — the same fail-open the server applies to
    // a venue whose timezone cannot be resolved.
    cutoffPassed: targets.envelope?.cutoffPassed ?? false,
    // A property of the CALLER, so read from the token rather than waited on.
    // The envelope's own `canOverrideCutoff` agrees; using the token means the
    // checkbox does not flicker in while targets load.
    canOverrideCutoff: hasPermission(Permission.OVERRIDE_REBOOK_CUTOFF),
    workshopWhen: time ? `${date} at ${time}` : date,
    onClose,
    onRebooked,
    escapeSuspended: browsing,
  });

  return {
    targets,
    month,
    setMonth,
    viewMode,
    setViewMode,
    browsing,
    openBrowse: () => setBrowsing(true),
    closeBrowse: () => setBrowsing(false),
    submit,
  };
}
